import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Clock,
  Users,
  Gauge,
  BarChart3,
  Sparkles,
  Plus,
} from 'lucide-react';
import PageHero from '../components/PageHero';
import VideoTestimonials from '../components/VideoTestimonials';
import SectionCTA from '../components/SectionCTA';
import CaseStudies from '../components/CaseStudies';
import TrustedByModule from '../components/TrustedByModule';
import ReadyToRankCTA from '../components/ReadyToRankCTA';
import SEO from '../components/SEO';

const whyPoints = [
  { icon: ShieldCheck, title: 'No Outsourcing', desc: 'All of our work is done in-house. From strategy to link building, nothing is outsourced.' },
  { icon: CheckCircle2, title: 'No Lock-Ins', desc: 'We do not require long-term commitments because we are confident in our service and in your success.' },
  { icon: Users, title: 'Real Experts Equals Real Results', desc: 'Our staff is composed of highly trained, well-qualified experts with the desire to help your business succeed.' },
  { icon: Clock, title: 'Weekly/Monthly Meetings', desc: 'We provide regular reporting sessions with your technical SEO lead and your account manager to guarantee your success.' },
  { icon: Sparkles, title: 'Your Local SEO Experts', desc: 'All of our SEO experts are highly qualified with impeccable skills and real-world experience.' },
  { icon: BarChart3, title: 'Real-time Reports', desc: 'All of our clients receive a custom dashboard to track their account in real-time.' },
];

const guaranteeCases = [
  {
    brand: 'DisplayMe',
    stat: '178',
    unit: 'keywords',
    desc: 'Ranked on page #1 of Google for: snap frames Sydney, real estate window displays, window display systems, brochure stand, light box display, poster frames online and many more. 80% traffic increase.',
  },
  {
    brand: 'Lashblossom',
    stat: '191',
    unit: 'keywords',
    desc: 'Ranked on page #1 of Google for: eyelash extensions Sydney, eyelash extension, mink eyelash extensions Sydney, microblading course, eyelash extension course and many more. 79% traffic increase.',
  },
  {
    brand: 'Style State',
    stat: '106',
    unit: 'keywords',
    desc: 'Ranked on page #1 of Google for: wholesale dresses australia, wholesale dresses, australia clothing wholesale, wholesale jackets, wholesale fashion and many more. 25% traffic increase.',
  },
];

const faqs = [
  {
    q: 'Are you an Australian based company?',
    a: 'Yes, absolutely. Green Web Marketing (GWM) is 100 percent Australian-owned. Our physical headquarters is in Sydney and we have branch offices in a number of other Australian and international cities. Our staff of technical experts, web designers and developers calls Sydney their home.',
  },
  {
    q: 'What is SEO?',
    a: 'Search Engine Optimisation (SEO) is the process of making your website attractive to search engines. This is accomplished by on-page optimisation, creating high quality backlinks and other SEO strategies, techniques and tactics. This marketing is focused on increasing your visibility through the use of organic search results. Effective SEO also encompasses the creation of websites that are easier for search engines to understand. This has the happy effect of creating websites that are easier for human beings to understand as well. Combining ease of use for people with an increased search engine appeal is the goal of good SEO. At Green Web Marketing we are dedicated to fine-tuning that principle to give you the best website possible.',
  },
  {
    q: 'Why does my website need SEO?',
    a: 'The bulk of traffic on the internet is driven by one of the major search engines like Google or Bing. By appealing to these search engines, websites can generate more hits and visits. Social media sites like Facebook, Reddit and others can generate interest in your product or service, but most users turn to search engines to find authoritative information about their interests. No matter what product you produce or service you provide, the bulk of your page visits will be generated by search engines. Optimizing your website to generate more visits will boost page visits and sales.',
  },
  {
    q: 'Can I do my own SEO?',
    a: "Sure, it is not rocket science, but – and this is a big but – SEO is complex and is constantly evolving. The rules that made web pages attractive to search engines five years ago do not have the same effect now. The basic concepts of SEO are simple and are helpful to know whether you are a business manager or web designer. If you are willing to learn the basics and devote time to create and maintain a SEO strategy for your company, then you are well on your way to becoming an SEO professional. However, depending on the results you want and the complexity of your website, SEO is a process that is best handled by professionals. From assigning keywords to creating content, an established company has an advantage over the most talented amateur. GWM is more than happy to help you learn SEO. Whether you want to create and manage your SEO on your own or use an experienced agency to aim for the front pages of Google, having a professional and proven company on your side will help your efforts immensely.",
  },
  {
    q: 'What makes SEO Sydney different from other companies?',
    a: 'SEO Sydney revels in the dichotomy of being a local company with a global reach. We understand our customers and their needs, whether they are in Sydney, Vietnam, the United States or Europe. We honed our skills at the local level and carried that hometown feeling with us when we expanded globally. All of our consultants are highly-trained and qualified professionals that want to make your business succeed. Our success rests on your success.',
  },
  {
    q: 'What are the most important factors for quality SEO?',
    a: 'Although the algorithm that search engines use is constantly evolving, there are five basic elements that generate high returns on web searches. These are: Clear and concise keywords research and keyword mapping, Proper technical structure, online and offline optimization and the proper use of meta-tags, High quality and original content, Consistently high-quality source links and backlinks leading to or from your website, A winning SEO strategy designed and implemented based on your needs. There are other factors that will influence your search ranking but these five have remained fairly consistent for the last couple of years.',
  },
  {
    q: 'What will SEO Sydney do for me?',
    a: 'Our company relies on giving the best service to our customers. We will optimize your website infrastructure, create content and establish links to increase your visibility. We create the conditions to maximize your online presence. All of these projects are fully documented and explained prior to implementation to keep your company aware of the full online and offline process. Specifically, since each company is unique, we custom make a plan for you. Contact us at 1800 006 296 for detailed information.',
  },
  {
    q: 'How much information will I be given about GWM SEO Sydney process?',
    a: 'All of it. Our consultants will keep you up-to-date on the project and your dashboard can be used for real time correlations between efforts and results. In addition to enhancing your current online marketing plan, we will show you how the process works. All of the work we do is transparent to our clients.',
  },
  {
    q: 'Do I have to sign a contract locking me in with your company for an extended period?',
    a: 'No. Absolutely not. We want you to be comfortable with our process. We create a plan based on what you want for your company, determine how long it takes and set it in motion. We let you know how long it will take to achieve the results you are looking for, but do not force you to sign a contract holding you to that time frame. If at any point you decide that our service is not what you need you can terminate it. Our success rate is high enough that we feel comfortable offering our service without a long-term contract.',
  },
  {
    q: 'Do you guarantee Front Page ranking?',
    a: 'Although the various search engine algorithms have the final say on where your page is ranked, we are confident enough in our process that we do offer a guarantee on our work. We can guarantee that sites built around specific keywords will be listed on the Front Page of Google. GWM SEO Sydney has the trained professionals and knowledge to make that happen. For detailed information about ROI (Return on Investment) contact us at 1800 006 296.',
  },
  {
    q: 'Can I choose my own keywords?',
    a: "Well, sort of. During our consultation we will go over your keywords and help create a set – including long-tailed keywords – that will give you the best results. Choosing the best keywords for your business, now and in the future, is part of our overall strategy to create the highest ROI possible.",
  },
  {
    q: 'How much does it cost?',
    a: 'That is a good question, and the answer is it depends on your business, the plan that we come up with and how much work is involved in the process. Since we believe that each customer is unique, we do not have a set rate for our services. For a quote, call us at 1800 006 296 and we can work out what you need and how much it will cost. FYI, we have very competitive pricing.',
  },
  {
    q: 'What kind of time frame does it take to reach the Front Page of Google?',
    a: 'There are so many factors that it is nearly impossible to give a time frame without knowing about your company. For websites that utilise competitive keywords it could take as long as six months or, for less competitive keywords, the time can be considerably less. Generally, getting to the front page will take between three and six months.',
  },
];

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        description="SEO Sydney GWM: ✅ No-Risk SEO Services In Sydney. 45+ experts. No Lock-in Contracts. Ethical SEO Company Trusted By Top Brands. Call 1800 006 296"
        canonical="https://www.agseosydney.com.au/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          url: 'https://www.agseosydney.com.au/',
          name: 'GWM — Green Web Marketing',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://www.agseosydney.com.au/?s={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      <PageHero
        eyebrow="GWM — SEO Sydney"
        title="UNMATCHABLE SEO RESULTS. WE SET THE INDUSTRY"
        titleItalic="STANDARDS"
        subtitle="REAL RESULTS — REAL PEOPLE — REALLY COMMITTED"
        ctaLabel="Get Started"
        ratingText="Trusted By 1000+ Australian Businesses"
      />

      <TrustedByModule />

      {/* UNMATCHABLE SEO RESULTS — Six guarantees */}
      <section className="py-20 md:py-24 bg-[#f8fafc]" data-testid="guarantees-grid">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="max-w-4xl mb-12">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#166534] mb-5">
              Unmatchable SEO results
            </p>
            <h2 className="font-['Outfit'] text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              The{' '}
              <Link to="/" className="hover:text-[#15803d] transition-colors" data-testid="seo-sydney-link-specialists">
                SEO Sydney
              </Link>{' '}
              <span className="italic font-serif text-[#15803d] font-medium">Specialists</span>
            </h2>
            <p className="text-xl md:text-2xl text-slate-600 mt-6 leading-relaxed">
              Work with an SEO agency that guarantees the success of its work.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                title: 'No Lock-in Contracts',
                desc: 'We operate on a month to month basis because we believe in providing the best value and service and letting the work speak for itself.',
              },
              {
                title: 'Results Guaranteed',
                desc: 'If we do not meet any of our keyword guarantees we will work, for free until we do!',
              },
              {
                title: 'Best SEO Service Guarantee',
                desc: "If you find an Australian company that provides better value, we'll match it and beat it by 10%.",
              },
              {
                title: 'No Minimum Notice Period',
                desc: 'You can pause your campaign with us at any time, just send us an email.',
              },
              {
                title: 'No Exit/Cancellation Fees',
                desc: 'You will never have to pay any cancellation/exit fee with us.',
              },
              {
                title: '100% Transparency',
                desc: 'Everything we do will be shared directly with you, we refuse to hide anything!',
              },
            ].map((g, i) => (
              <div
                key={i}
                className="group relative rounded-3xl bg-white border border-slate-200 p-7 md:p-8 hover:border-[#15803d]/40 hover:shadow-xl transition-all"
              >
                <div className="flex items-center gap-3 mb-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#15803d]/10 text-[#15803d]">
                    <CheckCircle2 size={18} />
                  </span>
                  <h3 className="text-lg md:text-xl font-bold text-slate-900 leading-tight">
                    {g.title}
                  </h3>
                </div>
                <p className="text-slate-600 leading-relaxed text-[15px]">{g.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GWM SEO SYDNEY Section */}
      <section id="about-us-section" className="py-24 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#166534] mb-4">
                About us
              </p>
              <h2 className="font-[\'Outfit\'] text-4xl md:text-5xl font-bold tracking-tight text-slate-900 leading-[1.05]">
                GWM{' '}
                <Link to="/" className="hover:text-[#15803d] transition-colors" data-testid="seo-sydney-link-about">
                  SEO{' '}
                  <span className="italic font-serif text-[#15803d] font-medium">Sydney</span>
                </Link>
              </h2>
            </div>
            <div className="lg:col-span-8">
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
                Do you remember the old commercial from Avis? They were number two so they tried harder. Our efforts are aimed at making us number one and we are not willing to settle for second best. Most businesses have the same ethos. We are counting on you to have that same drive and not be willing to settle for second best. To be number one means having a top-notch online marketing campaign that pulls customers to you and makes your competitors wonder how you got so savvy. Here is the secret, hiring the best company to get you the results you need. That is it; you hire the company with a proven track record of success and the best professional consultants in the industry and watch your online business take off. When you are ready to compete, we are ready to make you successful.
              </p>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed mt-6">
                If you are looking for an SEO Agency in Sydney that guarantees the results of its strategies, then GWM is the perfect match! We understand that it's hard to find a reliable digital marketing company in Sydney that you can trust. A quick search on Google will show there are hundreds of 'SEO experts' who promise first page results.
              </p>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed mt-6">
                At GWM SEO Agency Sydney, we are a specialist search engine optimisation agency working with small and large businesses to improve their online presence through search engine optimisation.
              </p>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed mt-6">
                An important part of delivering the best SEO services in Sydney, is our focus on your ROI and not just the traffic that we can generate for you. We'll focus our efforts on the search terms that will not only improve your rankings but also your profitability.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SectionCTA
        eyebrow="Stop settling for second best"
        title="GET REAL"
        titleItalic="RESULTS"
        ctaLabel="GET A QUOTE"
      />

      {/* WE HELP YOUR FUTURE CUSTOMERS Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="max-w-4xl mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#166534] mb-5">
              What we do
            </p>
            <h2 className="font-[\'Outfit\'] text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05] mb-6">
              WE HELP YOUR FUTURE CUSTOMERS{' '}
              <span className="italic font-serif text-[#15803d] font-medium">FIND YOU</span>
            </h2>
            <p className="text-xl md:text-2xl text-slate-600 leading-relaxed">
              Double Your Website Traffic, New Prospects and Clients Online in 120 Days.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'ADWORDS (PPC) MANAGEMENT',
                desc: 'Effective control of Pay-Per-Click (PPC) opportunities gives your business website three advantages. The first is a steady money stream, second is brand recognition while the third is strengthening your Search Engine Optimization (SEO) strategy. All three features are interconnected and vitally important to the success of the online aspect of your business.',
                icon: Gauge,
                href: '/pay-per-click-ppc-adwords-management',
              },
              {
                title: 'SEARCH ENGINE OPTIMISATION',
                desc: 'Keeping up with changes in SEO is a full-time job. GWM SEO Sydney feels so strongly about our abilities that we put it in our name. Driving business to your website and then keeping it there is what SEO is all about and there are no better companies to deliver the results that you need. Our professionals take a result-driven approach to generating and delivering strong SEO results for you.',
                icon: BarChart3,
                href: '/seo',
              },
              {
                title: 'ONLINE REPUTATION MANAGEMENT',
                desc: 'Everything we do is designed and executed to strengthen your online presence. Since every company is unique, our solution to getting you to the top will be handcrafted for your company. Each company working with us gets a custom strategy designed to take full advantage of the online market and build the brand name of the customer.',
                icon: ShieldCheck,
                href: '/online-reputation-management',
              },
            ].map((s, i) => (
              <Link
                key={i}
                to={s.href}
                className="group relative rounded-3xl bg-white border border-slate-200 p-8 md:p-10 shadow-sm hover:shadow-2xl hover:border-[#15803d]/40 hover:-translate-y-1 transition-all"
              >
                <div className="mb-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-green-50 border border-green-100 text-[#15803d]">
                  <s.icon size={28} />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-4 leading-snug">
                  {s.title}
                </h3>
                <p className="text-slate-600 leading-relaxed mb-6">{s.desc}</p>
                <span className="inline-flex items-center gap-2 text-[#15803d] font-semibold">
                  Explore More
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 120 DAY GUARANTEE Dark Section */}
      <section className="relative py-24 md:py-32 bg-slate-900 text-white overflow-hidden">
        <div className="pointer-events-none absolute -top-24 right-10 h-96 w-96 rounded-full bg-[#15803d]/15 blur-3xl" />
        <div className="container relative mx-auto px-6 md:px-12 max-w-7xl">
          <div className="max-w-4xl mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#86efac] mb-5">
              Our promise
            </p>
            <h2 className="font-[\'Outfit\'] text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-6">
              GWM&apos;S 120 DAY{' '}
              <span className="italic font-serif text-[#86efac] font-medium">GUARANTEE</span>
            </h2>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed">
              There are many Australian companies running around claiming they can get your website on the front page of Google within 90 days but they cannot back it up. Here is the real deal. We guarantee that that we will get you results within 120 days – 30 days longer, true, but a lot more realistic – and that we can show improved page results every step of the way and from the very first month.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {guaranteeCases.map((c, i) => (
              <div
                key={i}
                className="relative rounded-3xl bg-gradient-to-b from-slate-800 to-slate-800/60 border border-white/10 p-8 hover:border-[#15803d]/50 transition-all hover:-translate-y-1"
              >
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-6xl md:text-7xl font-black text-[#15803d] leading-none">
                    {c.stat}
                  </span>
                  <span className="text-xl font-semibold text-slate-400">{c.unit}</span>
                </div>
                <h3 className="text-2xl font-bold mb-3">{c.brand}</h3>
                <p className="text-slate-300 leading-relaxed text-sm">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY BUSINESSES CHOOSE GWM */}
      <section className="py-24 md:py-32 bg-[#f8fafc]">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="max-w-4xl mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#166534] mb-5">
              Why GWM
            </p>
            <h2 className="font-[\'Outfit\'] text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05] mb-4">
              WHY BUSINESSES{' '}
              <span className="italic font-serif text-[#15803d] font-medium">CHOOSE GWM</span>
            </h2>
            <h3 className="text-2xl md:text-3xl font-semibold text-[#15803d]">
              GWM IS RANKED #1 ON GOOGLE FOR SEO SYDNEY
            </h3>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyPoints.map((item, idx) => (
              <div
                key={idx}
                className="group rounded-3xl bg-white border border-slate-200 p-8 hover:shadow-xl hover:border-[#15803d]/40 hover:-translate-y-1 transition-all"
              >
                <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-green-50 border border-green-100 text-[#15803d]">
                  <item.icon size={22} />
                </div>
                <h4 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h4>
                <p className="text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CaseStudies />

      <VideoTestimonials
        heading="What Our Clients"
        headingItalic="Say"
        subheading="Some of our clients have been with us for over 10 years. Real results, real people, really committed."
      />

      {/* OUR PROCESS */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 max-w-6xl">
          <div className="max-w-4xl mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#166534] mb-5">
              How we work
            </p>
            <h2 className="font-[\'Outfit\'] text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              OUR{' '}
              <span className="italic font-serif text-[#15803d] font-medium">PROCESS</span>
            </h2>
          </div>

          <div className="space-y-10">
            {/* Part 1 */}
            <div className="relative rounded-3xl bg-gradient-to-br from-[#f8fafc] to-white border border-slate-200 p-10 md:p-14">
              <div className="absolute top-10 right-10 text-8xl md:text-9xl font-black text-slate-100 leading-none select-none">
                01
              </div>
              <div className="relative">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#166534] mb-3">
                  Part 1/3
                </p>
                <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
                  On-Site Optimisation Process
                </h3>
                <div className="space-y-6 text-slate-600 leading-relaxed">
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2">Market Research & Keyword Research:</h4>
                    <p>The first thing we do is conduct keyword research. We go through all possible keywords and identify the ones that will increase your website traffic and revenue. We identify 300 – 500 keywords in this stage. The purpose of this step is to get as many words as possible that we can use to identify aspects of your company&apos;s brand.</p>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2">Competitor Research:</h4>
                    <p>Our job is to beat your competition. We look at the keywords your competitors use and look for opportunities to target new keywords for your website. We set very broad standards for these keywords to identify as many of them as we possibly can.</p>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2">Keyword Mapping:</h4>
                    <p>Once we have the list of keywords, we use them to create a keyword mapping document that shows you titles, sub-titles, headings and content before mapping the keywords to the existing pages. We also identify the pages needed to be created to optimise the entirety of your website.</p>
                    <p className="mt-3 font-semibold text-slate-900">Note: This initial process takes around two weeks depending on the complexity and size of the website. Once we have gotten this far, we create a benchmark report and bring the detailed information to you for approval.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Part 2 */}
            <div className="relative rounded-3xl bg-gradient-to-br from-[#f8fafc] to-white border border-slate-200 p-10 md:p-14">
              <div className="absolute top-10 right-10 text-8xl md:text-9xl font-black text-slate-100 leading-none select-none">
                02
              </div>
              <div className="relative">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#166534] mb-3">
                  Part 2/3
                </p>
                <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">
                  On-Site Optimisation Process
                </h3>
                <div className="space-y-6 text-slate-600 leading-relaxed">
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2">Selecting keywords with high return for landing pages:</h4>
                    <p>From the landing page to the blog posts, we select and focus the best keywords to draw more attention from the search engines. We have the analytical tools to show us how strong the landing pages are and how strong your website is in relation to the rest of the internet.</p>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2">Technical Audit (25 – 30 pages):</h4>
                    <p>Once the keyword mapping document is complete, we do a technical audit on your website to ensure that the nuts and bolts aspects of your website are up to snuff. All technical aspects of your website including meta info, brand visibility, site speed, website security, technical problems, non-existent pages and gaps in content will be checked and listed at this stage.</p>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-slate-900 mb-2">Technical implementation:</h4>
                    <p>Once we have completed the audit and after your approval, we technically optimize the website to ensure it best complies with the search engine algorithms and that it can handle the increased traffic that will soon be generated.</p>
                    <p className="mt-3 font-semibold text-slate-900">Note: This initial process takes around two weeks depending on the complexity and size of the website. Once we have gotten this far, we create a benchmark report and bring the detailed information to you for approval.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Part 3 */}
            <div className="relative rounded-3xl bg-gradient-to-br from-[#f8fafc] to-white border border-slate-200 p-10 md:p-14">
              <div className="absolute top-10 right-10 text-8xl md:text-9xl font-black text-slate-100 leading-none select-none">
                03
              </div>
              <div className="relative">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#166534] mb-3">
                  Part 3/3
                </p>
                <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                  Off-Site Optimisation
                </h3>
                <p className="text-lg text-slate-600 mb-5">We feature your brand on niche related, high authority websites, blogs and magazines.</p>
                <p className="text-slate-600 mb-6">The preliminary steps are over and we are ready to begin building content and acquiring links. The ongoing process includes:</p>
                <div className="space-y-4 text-slate-600 leading-relaxed">
                  <p><strong className="text-slate-900">Popularity of the site:</strong> Increasing authority and popularity of your website</p>
                  <p><strong className="text-slate-900">Content Creation:</strong> Every month we create fresh content for your campaign and use the latest link building methods in order to promote your website.</p>
                  <p><strong className="text-slate-900">Get featured:</strong> We feature your brand on high quality websites, blogs and online magazines</p>
                  <p><strong className="text-slate-900">Link Building:</strong> this stage we also begin cultivating links from other websites that will reinforce your expertise.</p>
                  <p><strong className="text-slate-900">Frequent Strategy Review:</strong> We review the strategy a few times a week to make sure we are heading in the right direction.</p>
                  <p><strong className="text-slate-900">Monthly Reporting:</strong> At the end of every month, you receive an online report indicating all improvements and all the work that has been done for you during the month.</p>
                  <p><strong className="text-slate-900">Your dedicated account manager & technical SEO lead:</strong> Your dedicated account manager and technical SEO lead are present to report the improvements and achievements, answer any questions you might have and receive directions and feedback from you for the next month.</p>
                </div>
                <p className="mt-6 text-slate-900 font-semibold">Google loves websites that have high popularity and authority. Popular websites are talked about online in other popular places.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionCTA
        eyebrow="Be seen by customers searching now"
        title="GET YOUR BUSINESS ON PAGE 1 OF GOOGLE"
        titleItalic="FOR MAJOR KEYWORDS"
        ctaLabel="GET A QUOTE"
      />

      {/* FAQ Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 max-w-5xl">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#166534] mb-5">
              Questions & answers
            </p>
            <h2 className="font-[\'Outfit\'] text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              All your questions.{' '}
              <span className="italic font-serif text-[#15803d] font-medium">Answered.</span>
            </h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <details
                key={idx}
                open={idx === 0}
                className="group rounded-2xl bg-white border border-slate-200 hover:border-[#15803d]/40 hover:shadow-md transition-all overflow-hidden"
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none p-6 md:p-7">
                  <span className="text-lg md:text-xl font-bold text-slate-900">{faq.q}</span>
                  <span className="flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-full bg-[#15803d]/10 text-[#15803d] group-open:rotate-45 transition-transform">
                    <Plus size={20} />
                  </span>
                </summary>
                <div className="px-6 md:px-7 pb-7 text-slate-600 leading-relaxed border-t border-slate-100 pt-5">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final Section */}
      <section className="relative py-24 md:py-32 bg-slate-900 text-white overflow-hidden">
        <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[32rem] w-[32rem] rounded-full bg-[#15803d]/15 blur-3xl" />
        <div className="container relative mx-auto px-6 md:px-12 max-w-4xl text-center">
          <h2 className="font-[\'Outfit\'] text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-8">
            We are Your Leading{' '}
            <span className="italic font-serif text-[#86efac] font-medium">SEO Consultants</span> in Sydney
          </h2>
          <p className="text-lg md:text-xl text-slate-300 mb-12 leading-relaxed">
            Green Web Marketing is your leading SEO agency in Sydney. Contact us now for a free quote. We will help you beat your competition online through our strategic digital marketing. We will manage every part of your online business to deliver high ROI with rapid growth. We take full accountability and keep you up to date with detailed monthly reports so you can track your improvements making sure you are getting a high return on your investment. Call us now and let the best SEO agency in Sydney take care of the rest! When you are ready to compete, we are ready to make you successful.
          </p>
          <Link
            to="/contact"
            className="group inline-flex items-center gap-2 rounded-full bg-[#db2777] hover:bg-[#be185d] text-white px-12 py-5 font-bold text-lg shadow-2xl shadow-[#db2777]/30 transition-all hover:-translate-y-0.5"
          >
            GET A QUOTE
            <ArrowRight size={22} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <ReadyToRankCTA />
    </div>
  );
};

export default Home;
